import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { Calendar, Clock, LogOut, User, ChevronRight, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useAuth } from '../context/AuthContext';
import { appointmentsAPI } from '../lib/api';
import { toast } from 'sonner';

const TeamDashboard = () => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState('today');

    useEffect(() => {
        loadAppointments();
    }, [dateFilter]);

    const loadAppointments = async () => {
        try {
            const params = {};
            const today = new Date();
            
            if (dateFilter === 'today') {
                params.date = format(today, 'yyyy-MM-dd');
            } else if (dateFilter === 'tomorrow') {
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                params.date = format(tomorrow, 'yyyy-MM-dd');
            }
            // 'all' = no date filter
            
            const response = await appointmentsAPI.getAll(params);
            setAppointments(response.data);
        } catch (error) {
            console.error('Failed to load appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await appointmentsAPI.updateStatus(id, status);
            toast.success('Status updated');
            loadAppointments();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-yellow-500/20 text-yellow-400';
            case 'confirmed': return 'bg-green-500/20 text-green-400';
            case 'completed': return 'bg-cyan-500/20 text-cyan-400';
            case 'cancelled': return 'bg-pink-500/20 text-pink-400';
            default: return 'bg-zinc-500/20 text-zinc-400';
        }
    };

    const getDateLabel = (dateStr) => {
        const date = parseISO(dateStr);
        if (isToday(date)) return 'Today';
        if (isTomorrow(date)) return 'Tomorrow';
        return format(date, 'EEE, MMM d');
    };

    // Group appointments by date
    const groupedAppointments = appointments.reduce((acc, apt) => {
        if (!acc[apt.date]) acc[apt.date] = [];
        acc[apt.date].push(apt);
        return acc;
    }, {});

    // Sort dates
    const sortedDates = Object.keys(groupedAppointments).sort();

    return (
        <div className="min-h-screen bg-dark" data-testid="team-dashboard">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-dark border-b border-zinc-800 px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <img
                            src="https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/03303zlc_96f21524-c6ba-4411-86fb-3aa42208d697.jpeg"
                            alt="RL"
                            className="h-10 w-10"
                        />
                        <div>
                            <h1 className="font-anton text-lg">MY SCHEDULE</h1>
                            <p className="text-zinc-500 text-sm font-urbanist">{user?.name}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="text-zinc-500 hover:text-white font-urbanist text-sm hidden sm:block">
                            View Site
                        </Link>
                        <button
                            onClick={handleLogout}
                            data-testid="team-logout-btn"
                            className="flex items-center gap-2 text-zinc-500 hover:text-white font-urbanist"
                        >
                            <LogOut size={18} />
                            <span className="hidden sm:inline">Logout</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-6 py-8">
                {/* Filter */}
                <div className="flex items-center justify-between mb-8">
                    <h2 className="font-anton text-2xl">APPOINTMENTS</h2>
                    <Select value={dateFilter} onValueChange={setDateFilter}>
                        <SelectTrigger className="w-40 bg-dark-paper border-zinc-700" data-testid="date-filter">
                            <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="today">Today</SelectItem>
                            <SelectItem value="tomorrow">Tomorrow</SelectItem>
                            <SelectItem value="all">All Upcoming</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Appointments List */}
                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-dark-paper border border-zinc-800 p-6 animate-pulse">
                                <div className="h-6 bg-zinc-800 rounded w-1/3 mb-4" />
                                <div className="h-4 bg-zinc-800 rounded w-1/2" />
                            </div>
                        ))}
                    </div>
                ) : appointments.length === 0 ? (
                    <div className="text-center py-16">
                        <Calendar className="mx-auto text-zinc-700 mb-4" size={48} />
                        <h3 className="font-anton text-xl text-zinc-500 mb-2">NO APPOINTMENTS</h3>
                        <p className="text-zinc-600 font-urbanist">
                            {dateFilter === 'today' ? "You have no appointments for today." : "No appointments found."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {sortedDates.map((date) => (
                            <div key={date}>
                                <h3 className="font-urbanist font-semibold text-zinc-400 mb-4 flex items-center gap-2">
                                    <Calendar size={16} />
                                    {getDateLabel(date)}
                                </h3>
                                <div className="space-y-4">
                                    {groupedAppointments[date]
                                        .sort((a, b) => a.time.localeCompare(b.time))
                                        .map((apt, i) => (
                                            <motion.div
                                                key={apt.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.05 }}
                                                className="bg-dark-paper border border-zinc-800 p-6 card-hover"
                                                data-testid={`team-appointment-${apt.id}`}
                                            >
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                    <div className="flex items-start gap-4">
                                                        {/* Time */}
                                                        <div className="text-center min-w-[60px]">
                                                            <p className="font-anton text-2xl text-primary">{apt.time}</p>
                                                        </div>

                                                        {/* Details */}
                                                        <div>
                                                            <h4 className="font-urbanist font-semibold text-white text-lg">
                                                                {apt.customer_name}
                                                            </h4>
                                                            <p className="text-zinc-400 font-urbanist">
                                                                {apt.service_name}
                                                                <span className="text-primary ml-2">€{apt.service_price}</span>
                                                            </p>
                                                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-zinc-500">
                                                                <span className="flex items-center gap-1">
                                                                    <Phone size={14} />
                                                                    {apt.customer_phone}
                                                                </span>
                                                                <span className="flex items-center gap-1">
                                                                    <Mail size={14} />
                                                                    {apt.customer_email}
                                                                </span>
                                                            </div>
                                                            {apt.notes && (
                                                                <p className="text-zinc-600 text-sm mt-2 italic">
                                                                    Note: {apt.notes}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Status & Actions */}
                                                    <div className="flex items-center gap-3 sm:flex-col sm:items-end">
                                                        <span className={`px-3 py-1 text-xs font-semibold uppercase ${getStatusColor(apt.status)}`}>
                                                            {apt.status}
                                                        </span>
                                                        {apt.status === 'confirmed' && (
                                                            <Button
                                                                size="sm"
                                                                onClick={() => updateStatus(apt.id, 'completed')}
                                                                className="bg-accent-cyan text-black font-urbanist font-semibold"
                                                            >
                                                                Mark Complete
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default TeamDashboard;
